/**
 * Debtor records are aggregated from demands (matters) and optional
 * standalone profiles created before any demand exists.
 *
 * When an Account # is present, contacts consolidate under that number so
 * the same customer is one debtor even if name/company spelling differs.
 */

/**
 * Normalize account numbers for matching (trim, casefold, drop spaces).
 * @param {unknown} value
 */
export function normalizeAccountNumber(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

/**
 * Read account # from matter, profile, or form-shaped objects.
 * @param {object} fields
 */
export function debtorAccountNumber(fields = {}) {
  if (!fields || typeof fields !== 'object') return '';
  const raw =
    fields.External_Account_Number ??
    fields.externalAccountNumber ??
    fields.accountNumber ??
    fields.Account_Number ??
    '';
  return String(raw || '').trim();
}

/**
 * Legacy identity key used when no account # is available.
 * @param {object} matterOrFields
 */
export function debtorLegacyRecordKey(matterOrFields = {}) {
  const name = String(matterOrFields.Debtor_Name || matterOrFields.name || '')
    .trim()
    .toLowerCase();
  const company = String(
    matterOrFields.Debtor_Company ||
      matterOrFields.company ||
      matterOrFields.Debtor_Name ||
      matterOrFields.name ||
      ''
  )
    .trim()
    .toLowerCase();
  const state = String(matterOrFields.Debtor_State || matterOrFields.state || '').trim();
  return `${name}|${company}|${state}`;
}

/**
 * Debtor record id. Prefers Account # when set so contacts consolidate.
 * @param {object} matterOrFields
 */
export function debtorRecordKey(matterOrFields = {}) {
  const account = normalizeAccountNumber(debtorAccountNumber(matterOrFields));
  if (account) return `acct|${account}`;
  return debtorLegacyRecordKey(matterOrFields);
}

export function encodeDebtorId(id) {
  return encodeURIComponent(String(id || ''));
}

export function decodeDebtorId(param) {
  if (param == null) return '';
  try {
    return decodeURIComponent(String(param));
  } catch {
    return String(param);
  }
}

function matterUpdatedAt(matter) {
  const raw = matter?.Updated_At || matter?.Created_At || '';
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : 0;
}

function emptyTotals() {
  return { totalOwed: 0, openCount: 0, recovered: 0, matters: [] };
}

function blankRecord(id, seed = {}) {
  return {
    id: String(id),
    name: seed.name || seed.company || '—',
    company: seed.company || seed.name || '',
    state: seed.state || '',
    country: seed.country || '',
    street: seed.street || '',
    city: seed.city || '',
    postalCode: seed.postalCode || '',
    email: seed.email || '',
    accountNumber: seed.accountNumber || '',
    notes: seed.notes || '',
    notesEntries: Array.isArray(seed.notesEntries) ? seed.notesEntries : [],
    ...emptyTotals(),
    isProfileOnly: true,
  };
}

function absorbRecord(target, source) {
  if (!target || !source) return target;
  target.matters = [...(target.matters || []), ...(source.matters || [])];
  target.totalOwed += Number(source.totalOwed || 0);
  target.openCount += Number(source.openCount || 0);
  target.recovered += Number(source.recovered || 0);
  target.isProfileOnly = target.isProfileOnly && source.isProfileOnly;
  target.name = target.name || source.name || '';
  target.company = target.company || source.company || '';
  target.state = target.state || source.state || '';
  target.country = target.country || source.country || '';
  target.street = target.street || source.street || '';
  target.city = target.city || source.city || '';
  target.postalCode = target.postalCode || source.postalCode || '';
  target.email = target.email || source.email || '';
  target.accountNumber = target.accountNumber || source.accountNumber || '';
  if (!target.notes && source.notes) target.notes = source.notes;
  if (
    (!target.notesEntries || !target.notesEntries.length) &&
    Array.isArray(source.notesEntries) &&
    source.notesEntries.length
  ) {
    target.notesEntries = source.notesEntries;
  }
  return target;
}

/**
 * Build sorted debtor records from matters + optional standalone profiles.
 * Matters/profiles that share an Account # collapse into one contact record.
 */
export function buildDebtorRecords(matters = [], profiles = []) {
  const map = new Map();

  for (const profile of profiles || []) {
    if (!profile?.id) continue;
    const accountNumber = debtorAccountNumber(profile);
    const key = debtorRecordKey({
      ...profile,
      Debtor_Name: profile.name,
      Debtor_Company: profile.company,
      Debtor_State: profile.state,
      External_Account_Number: accountNumber,
    });
    if (!key || key === '||') continue;
    const existing = map.get(key);
    const seeded = blankRecord(key, {
      name: profile.name || profile.company || '—',
      company: profile.company || profile.name || '',
      state: profile.state || '',
      country: profile.country || '',
      street: profile.street || '',
      city: profile.city || '',
      postalCode: profile.postalCode || '',
      email: profile.email || '',
      accountNumber,
      notes: profile.notes || '',
      notesEntries: Array.isArray(profile.notesEntries) ? profile.notesEntries : [],
    });
    if (!existing) {
      map.set(key, seeded);
    } else {
      absorbRecord(existing, seeded);
      existing.accountNumber = existing.accountNumber || accountNumber;
    }
  }

  for (const m of matters || []) {
    if (!m) continue;
    const key = debtorRecordKey(m);
    if (!key || key === '||') continue;
    const amount = Number(m.Amount_Owed || 0);
    const paid =
      String(m.Status || '').toUpperCase() === 'PAID' ||
      String(m.Status || '').toUpperCase() === 'PAID_BEFORE_ASSIGNMENT';
    const accountNumber = debtorAccountNumber(m);
    const existing = map.get(key);

    if (!existing) {
      map.set(key, {
        ...blankRecord(key, {
          name: m.Debtor_Name || m.Debtor_Company || '—',
          company: m.Debtor_Company || m.Debtor_Name || '',
          state: m.Debtor_State || '',
          country: m.Debtor_Country || '',
          street: m.Debtor_Street || '',
          city: m.Debtor_City || '',
          postalCode: m.Debtor_Postal_Code || '',
          email: m.Debtor_Email || '',
          accountNumber,
        }),
        matters: [m],
        totalOwed: amount,
        openCount: paid ? 0 : 1,
        recovered: paid ? amount : 0,
        isProfileOnly: false,
      });
    } else {
      existing.matters.push(m);
      existing.totalOwed += amount;
      if (paid) existing.recovered += amount;
      else existing.openCount += 1;
      existing.isProfileOnly = false;
      existing.name = existing.name || m.Debtor_Name || '';
      existing.company = existing.company || m.Debtor_Company || '';
      existing.state = existing.state || m.Debtor_State || '';
      existing.country = existing.country || m.Debtor_Country || '';
      existing.street = existing.street || m.Debtor_Street || '';
      existing.city = existing.city || m.Debtor_City || '';
      existing.postalCode = existing.postalCode || m.Debtor_Postal_Code || '';
      existing.email = existing.email || m.Debtor_Email || '';
      existing.accountNumber = existing.accountNumber || accountNumber;
    }
  }

  // Fold name-only groups into an Account # group when they share the same
  // legacy contact identity (so adding Account # consolidates prior records).
  const accountRecords = [...map.entries()].filter(([id]) => String(id).startsWith('acct|'));
  for (const [legacyId, legacyRecord] of [...map.entries()]) {
    if (String(legacyId).startsWith('acct|')) continue;
    const match = accountRecords.find(([, accountRecord]) => {
      const accountLegacy = debtorLegacyRecordKey({
        Debtor_Name: accountRecord.name,
        Debtor_Company: accountRecord.company,
        Debtor_State: accountRecord.state,
      });
      if (accountLegacy === legacyId) return true;
      return (accountRecord.matters || []).some(
        (matter) => debtorLegacyRecordKey(matter) === legacyId
      );
    });
    if (!match) continue;
    absorbRecord(match[1], legacyRecord);
    map.delete(legacyId);
  }

  const records = Array.from(map.values()).map((record) => {
    const mattersSorted = [...(record.matters || [])].sort(
      (a, b) => matterUpdatedAt(b) - matterUpdatedAt(a)
    );
    const primary = mattersSorted[0] || null;
    const accountNumber =
      record.accountNumber ||
      debtorAccountNumber(primary) ||
      mattersSorted.map(debtorAccountNumber).find(Boolean) ||
      '';
    return {
      ...record,
      matters: mattersSorted,
      name: primary?.Debtor_Name || record.name,
      company: primary?.Debtor_Company || record.company,
      state: primary?.Debtor_State || record.state,
      country: primary?.Debtor_Country || record.country,
      street: primary?.Debtor_Street || record.street,
      city: primary?.Debtor_City || record.city,
      postalCode: primary?.Debtor_Postal_Code || record.postalCode,
      email: primary?.Debtor_Email || record.email,
      accountNumber,
      primaryMatter: primary,
      isProfileOnly: mattersSorted.length === 0,
    };
  });

  return records.sort((a, b) =>
    String(a.name || a.company || '').localeCompare(String(b.name || b.company || ''))
  );
}

export function findDebtorRecord(debtors, debtorId) {
  const id = decodeDebtorId(debtorId);
  if (!id) return null;
  return (debtors || []).find((d) => String(d.id) === id) || null;
}
